import { useState, useMemo } from 'react'
import { useChat } from '../contexts/ChatContext'
import { Row, avatarColorIndex, SearchBar, FilterBar, FilterPill, EmptyState } from '@ofself/shell'

// THE kit hash — one hue per name, app aliases map onto the kit palette.
const hashColor = (name) => `var(--avatar-${avatarColorIndex(name)})`

export default function DirectoriesPage({ selectedMember, onSelectMember }) {
  const { members, channels, groups, groupChannelsByGroup, currentUser } = useChat()
  const [filter, setFilter] = useState('people')
  const [search, setSearch] = useState('')

  const q = search.toLowerCase().trim()

  const filteredMembers = useMemo(() => {
    if (!members) return []
    const list = members.filter(m => m.user_id !== currentUser?.id)
    if (!q) return list
    return list.filter(m => (m.username || '').toLowerCase().includes(q))
  }, [members, currentUser, q])

  const filteredChannels = useMemo(() => {
    if (!channels) return []
    const list = channels.filter(c => !c.is_dm)
    if (!q) return list
    return list.filter(c => (c.name || '').toLowerCase().includes(q))
  }, [channels, q])

  const filteredGroups = useMemo(() => {
    if (!groups) return []
    if (!q) return groups
    return groups.filter(g => (g.name || '').toLowerCase().includes(q))
  }, [groups, q])

  const filters = ['People', 'Groups', 'Channels']

  return (
    <div className="sidebar">
      <SearchBar
        value={search}
        onChange={e => setSearch(e.target.value)}
        onClear={() => setSearch('')}
        placeholder={`Search ${filter}...`}
      />

      <FilterBar>
        {filters.map(f => (
          <FilterPill key={f} active={filter === f.toLowerCase()} onClick={() => { setFilter(f.toLowerCase()); setSearch('') }}>
            {f}
          </FilterPill>
        ))}
      </FilterBar>

      <ul className="block__body block-scroll list-rows">
        {filter === 'people' && filteredMembers.map(m => (
          <Row as="li"
            key={m.user_id}
            className="dir-row"
            active={selectedMember?.user_id === m.user_id}
            onClick={() => onSelectMember(m)}
          >
            <div className="convo-avatar" style={{ background: `color-mix(in srgb, ${hashColor(m.username)} 16%, transparent)`, color: hashColor(m.username) }}>
              {(m.username || '?')[0].toUpperCase()}
            </div>
            <div className="list-row__body">
              <div className="list-row__name">{m.username}</div>
            </div>
          </Row>
        ))}

        {filter === 'channels' && filteredChannels.map(c => (
          <Row as="li"
            key={c.id}
            className="dir-row"
            onClick={() => onSelectMember({ _type: 'channel', ...c })}
          >
            <div className="convo-avatar convo-avatar-channel">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 9h16M4 15h16M10 3l-2 18M16 3l-2 18" />
              </svg>
            </div>
            <div className="list-row__body">
              <div className="list-row__name">{c.name}</div>
              {c.group_id && <div className="list-row__preview">{groups?.find(g => g.id === c.group_id)?.name}</div>}
            </div>
          </Row>
        ))}

        {filter === 'groups' && filteredGroups.map(g => {
          const gc = groupChannelsByGroup?.[g.id] || []
          return (
            <Row as="li"
              key={g.id}
              className="dir-row"
              onClick={() => onSelectMember({ _type: 'group', ...g, _channelCount: gc.length })}
            >
              <div className="convo-avatar convo-avatar-channel">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <div className="list-row__body">
                <div className="list-row__name">{g.name}</div>
                <div className="list-row__preview">
                  {gc.length} channel{gc.length !== 1 ? 's' : ''}
                  {g.members ? ` \u00B7 ${g.members.length} member${g.members.length !== 1 ? 's' : ''}` : ''}
                </div>
              </div>
            </Row>
          )
        })}

        {filter === 'people' && filteredMembers.length === 0 && (
          <EmptyState as="li">No people found</EmptyState>
        )}
        {filter === 'channels' && filteredChannels.length === 0 && (
          <EmptyState as="li">No channels found</EmptyState>
        )}
        {filter === 'groups' && filteredGroups.length === 0 && (
          <EmptyState as="li">No groups found</EmptyState>
        )}
      </ul>
    </div>
  )
}

export function DirectoryDetail({ member, onStartDM, onNavigateChannel }) {
  const { dmChannels, createDM, selectChannel, groups, groupChannelsByGroup } = useChat()

  if (!member) {
    return (
      <div className="dir-detail-empty">
        Select a person, channel, or group to view details.
      </div>
    )
  }

  if (member._type === 'channel') {
    const group = groups?.find(g => g.id === member.group_id)
    return (
      <div className="dir-detail">
        <div className="dir-detail-header">
          <div className="dir-detail-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 9h16M4 15h16M10 3l-2 18M16 3l-2 18" />
            </svg>
          </div>
          <h2 className="dir-detail-name">{member.name}</h2>
          {group && <div className="dir-detail-sub">{group.name}</div>}
        </div>
        <button className="dir-detail-action" onClick={() => { selectChannel(member); if (onNavigateChannel) onNavigateChannel() }}>
          Open Channel
        </button>
      </div>
    )
  }

  if (member._type === 'group') {
    const gc = groupChannelsByGroup?.[member.id] || []
    return (
      <div className="dir-detail">
        <div className="dir-detail-header">
          <div className="dir-detail-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
          <h2 className="dir-detail-name">{member.name}</h2>
          <div className="dir-detail-sub">
            {member.members?.length || 0} members
          </div>
        </div>
        {gc.length > 0 && (
          <div className="dir-detail-section">
            <div className="dir-detail-section-title ofself-label">Channels</div>
            {gc.map(c => (
              <button key={c.id} className="dir-detail-channel" onClick={() => { selectChannel(c); if (onNavigateChannel) onNavigateChannel() }}>
                <span>#</span> {c.name}
              </button>
            ))}
          </div>
        )}
      </div>
    )
  }

  // Person
  async function handleDM() {
    const existing = dmChannels?.find(dm => dm.other_user_id === member.user_id || dm.other_username === member.username)
    if (existing) {
      selectChannel(existing)
    } else {
      await createDM(member.user_id)
    }
    if (onStartDM) onStartDM()
  }

  return (
    <div className="dir-detail">
      <div className="dir-detail-header">
        <div className="dir-detail-avatar" style={{ background: `var(--avatar-${avatarColorIndex(member.username)})` }}>
          {(member.username || '?')[0].toUpperCase()}
        </div>
        <h2 className="dir-detail-name">{member.username}</h2>
      </div>
      <button className="dir-detail-action" onClick={handleDM}>
        Message
      </button>
    </div>
  )
}
